import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'react-bootstrap';
import Select, { MultiValue } from 'react-select';
import { ModalBox, RoundedAvatar } from '../../../../../components';
import { customStyles } from '../../../../../components/modal/MemberModal';
import { useAppSelector } from '../../../../../hooks/redux';
import { IMatch } from '../PlayTag/PlayTag';
import { Team, User } from '../../../../../types/tournament';
import { imgOptionUserLabel } from '../../../../../utils/imgOptionUserLabel';

function buildClassUserLookup(teams: Team[] = []): Map<number, User> {
    const lookup = new Map<number, User>();
    for (const classTeam of teams) {
        for (const user of classTeam.users ?? []) {
            if (typeof user !== 'number' && user?.id) {
                lookup.set(user.id, user);
            }
        }
    }
    return lookup;
}

function resolveTeamRoster(team: Team | undefined, userLookup: Map<number, User>): User[] {
    if (!team) {
        return [];
    }
    if (team.users?.length) {
        return team.users
            .map((user) => (typeof user === 'number' ? userLookup.get(user) : user))
            .filter((user): user is User => Boolean(user?.id));
    }
    const userIds = (team as Team & { userIds?: number[] }).userIds;
    if (userIds?.length) {
        return userIds
            .map((id) => userLookup.get(id))
            .filter((user): user is User => Boolean(user?.id));
    }
    return [];
}

export const MatchPlayerModal = (props: ModalProps) => {
    const { t } = useTranslation();
    const { classCollection, currentClassId } = useAppSelector((state) => state.class);
    const { matches, setMatches, teamId, fieldName, matchId } = useAppSelector(
        (state) => state.modals.data
    );

    const prefix = 'modal-';
    const inputPrefix = 'input-';

    const [multiSelections, setMultiSelections] = useState<User[]>([]);
    const classData = classCollection.find((el) => el.classId === currentClassId);
    const team = classData?.teams.find((team) => team.id === teamId);
    const userLookup = useMemo(() => buildClassUserLookup(classData?.teams), [classData?.teams]);
    const rosterUsers = useMemo(() => resolveTeamRoster(team, userLookup), [team, userLookup]);
    const userOptions = rosterUsers.map((user) => imgOptionUserLabel(user));

    //@ts-ignore
    const tournamentMatches = Object.values(matches).map((match: IMatch) =>
        match.id === matchId
            ? {
                  ...match,
                  [fieldName + 1]: multiSelections[0] || null,
                  [fieldName + 2]: multiSelections[1] || null,
              }
            : match
    );

    const handleSelect = useCallback(
        (e: MultiValue<{ value: string; label: string }>) => {
            const users = e.map((option) => {
                return rosterUsers.find((user) => user.id === Number(option.value)) || ({} as User);
            });

            setMultiSelections(users);
        },
        [rosterUsers]
    );

    const handleSave = useCallback(() => {
        multiSelections.length &&
            setMatches(
                'tournamentMatches',
                tournamentMatches.reduce((acc: any, item) => {
                    acc[`${item.id}`] = item;
                    return acc;
                }, {})
            );
        props.onHide && props.onHide();
    }, [multiSelections.length, props, setMatches, tournamentMatches]);

    return (
        <ModalBox {...props} title={t(prefix + 'addPlayers2')} handleSave={handleSave}>
            <Select
                styles={customStyles}
                isMulti
                name="players"
                placeholder={t(inputPrefix + 'placeholderSelect')}
                options={userOptions}
                className="basic-multi-select holder-select"
                classNamePrefix="select"
                // @ts-ignore
                onChange={handleSelect}
                formatOptionLabel={(data: any) => (
                    <div className="d-flex align-items-center">
                        <RoundedAvatar avatar={data.avatar} alt="User avatar" name="user" />
                        {data.label}
                    </div>
                )}
            />
        </ModalBox>
    );
};
